/**
 * ArticleScanPage (Jul-2026, User-Wunsch) — mobile, LOGIN-FREIE Barcode-Nacherfassung.
 * Wird per QR-Code aus der Warenwirtschaft geöffnet (Token in der URL).
 * Ablauf: Artikel suchen/auswählen → Barcode mit Handy-Kamera scannen
 * (alle Formate via @zxing) → wird dem Artikel zugeordnet.
 * Route: /artikel-scan/:token
 */
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ScanLine, Search, Check, AlertCircle, Package } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import EanCameraScanner from "../components/wms/EanCameraScanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArticleScanPage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [searching, setSearching] = useState(false);
    const [scanFor, setScanFor] = useState(null);
    const [done, setDone] = useState(0);
    // Jul-2026 (User-Wunsch) — SKU-Direkteingabe: Artikelnummer eintippen →
    // Artikel prüfen → Scan-Code mit der Kamera erfassen & speichern.
    const [sku, setSku] = useState("");
    const [skuArt, setSkuArt] = useState(null);
    const [skuBusy, setSkuBusy] = useState(false);
    const [skuScan, setSkuScan] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);

    const loadInfo = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/public/article-scan/${token}`);
            setInfo(data); setDone(data.assigned_count || 0);
        } catch (e) {
            setError(e.response?.data?.detail || "Scan-Sitzung ungültig oder abgelaufen.");
        } finally { setLoading(false); }
    }, [token]);

    const search = useCallback(async () => {
        setSearching(true);
        try {
            const { data } = await axios.get(`${API}/public/article-scan/${token}/articles`, { params: { q, limit: 30 } });
            setItems(data.items || []);
        } catch { /* noop */ }
        finally { setSearching(false); }
    }, [token, q]);

    useEffect(() => { loadInfo(); }, [loadInfo]);
    useEffect(() => { if (info) search(); }, [info]); // eslint-disable-line react-hooks/exhaustive-deps

    // SKU-Direkteingabe: exakte Artikelnummer laden, dann Kamera öffnen.
    const lookupSku = async () => {
        const val = sku.trim();
        if (!val) { toast.error("Bitte Artikelnummer eingeben"); return; }
        setSkuBusy(true);
        setSkuArt(null);
        setLastSaved(null);
        try {
            const { data } = await axios.get(`${API}/public/article-scan/${token}/by-sku`, { params: { sku: val } });
            setSkuArt(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kein Artikel mit dieser Artikelnummer gefunden");
        } finally { setSkuBusy(false); }
    };

    const saveSkuEan = async (code, overwrite = false) => {
        const ean = (code || "").trim();
        if (!ean) { toast.error("Kein Barcode erkannt"); return; }
        try {
            await axios.post(`${API}/public/article-scan/${token}/assign`,
                { article_id: skuArt.id, ean, overwrite });
            setLastSaved({ name: skuArt.name, sku: skuArt.sku, ean });
            setSkuScan(false);
            setSkuArt(null);
            setSku("");
            setDone((d) => d + 1);
            toast.success(`Gespeichert: ${ean}`);
        } catch (e) {
            const d = e.response?.data?.detail;
            if (d?.code === "has_ean") {
                setSkuScan(false);
                if (window.confirm(`${d.message}`)) await saveSkuEan(ean, true);
                return;
            }
            setSkuScan(false);
            toast.error(typeof d === "object" ? d.message : (d || "Speichern fehlgeschlagen"));
        }
    };

    const assign = async (article, code) => {
        const ean = (code || "").trim();
        if (!ean) { toast.error("Kein Barcode erkannt"); return; }
        try {
            await axios.post(`${API}/public/article-scan/${token}/assign`, { article_id: article.id, ean });
            toast.success(`Gespeichert: ${ean}`);
            setItems((prev) => prev.filter((a) => a.id !== article.id));
            setDone((d) => d + 1);
            setScanFor(null);
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "object" ? d.message : (d || "Speichern fehlgeschlagen"));
        }
    };

    if (loading) return <div className="min-h-screen bg-[#0E0E10] flex items-center justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Lädt…</div>;

    if (error) return (
        <div className="min-h-screen bg-[#0E0E10] flex items-center justify-center px-4">
            <div className="bg-white rounded-sm p-6 max-w-sm text-center" data-testid="artscan-error">
                <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                <p className="font-bold text-[#111827]">Nicht verfügbar</p>
                <p className="text-sm text-gray-600 mt-1">{error}</p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50" data-testid="article-scan-page">
            <div className="bg-[#111827] text-white sticky top-0 z-30">
                <div className="max-w-lg mx-auto px-4 py-3">
                    <div className="flex items-center gap-2">
                        <ScanLine className="w-5 h-5 text-[#FFCC00]" />
                        <div className="min-w-0">
                            <div className="font-display font-black text-lg leading-tight">Barcodes erfassen</div>
                            <div className="text-[11px] text-gray-400 truncate">{info?.dealer_label}</div>
                        </div>
                        <span className="ml-auto text-xs bg-emerald-600 px-2 py-1 rounded-sm font-bold" data-testid="artscan-progress">
                            <Check className="w-3 h-3 inline mr-0.5" />{done}
                        </span>
                    </div>
                </div>
            </div>

            <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
                {/* Jul-2026 (User-Wunsch) — SKU-Direkteingabe */}
                <div className="bg-[#111827] rounded-sm p-3" data-testid="artscan-sku-block">
                    <div className="text-[11px] font-black uppercase tracking-wider text-[#FFCC00] mb-1.5">Artikelnummer direkt eingeben</div>
                    <div className="flex gap-2">
                        <Input value={sku} onChange={(e) => setSku(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && lookupSku()}
                            placeholder="Artikelnummer (SKU)" autoCapitalize="off" autoCorrect="off"
                            className="h-11 bg-white border-2 border-[#FFCC00] flex-1" data-testid="artscan-sku-input" />
                        <Button onClick={lookupSku} disabled={skuBusy} className="h-11 bg-[#FFCC00] text-[#111827] font-bold hover:brightness-95" data-testid="artscan-sku-btn">
                            {skuBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        </Button>
                    </div>
                    {skuArt && (
                        <div className="mt-2 bg-white rounded-sm p-3" data-testid="artscan-sku-card">
                            <div className="font-bold text-sm text-[#111827]">{skuArt.name}</div>
                            <div className="text-[11px] text-gray-500">
                                {[skuArt.sku, skuArt.manufacturer, skuArt.location].filter(Boolean).join(" · ")}
                            </div>
                            <div className="text-[11px] mt-0.5">
                                {skuArt.ean
                                    ? <span className={skuArt.ean_provisional || skuArt.ean_internal ? "text-amber-700 font-bold" : "text-gray-600"}>
                                        Aktueller Code: {skuArt.ean}{skuArt.ean_provisional ? " (vorläufig)" : skuArt.ean_internal ? " (intern)" : ""}
                                    </span>
                                    : <span className="text-gray-400">Noch kein Barcode hinterlegt</span>}
                            </div>
                            <Button onClick={() => setSkuScan(true)} className="w-full h-12 mt-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="artscan-sku-scan-btn">
                                <ScanLine className="w-4 h-4 mr-1.5" /> Scan-Code jetzt erfassen
                            </Button>
                            {skuScan && (
                                <EanCameraScanner onDetected={(code) => saveSkuEan(code)} onClose={() => setSkuScan(false)} />
                            )}
                        </div>
                    )}
                    {lastSaved && !skuArt && (
                        <div className="mt-2 bg-emerald-50 border border-emerald-300 rounded-sm p-2.5 text-[12px] text-emerald-800 flex items-center gap-2" data-testid="artscan-sku-saved">
                            <Check className="w-4 h-4 shrink-0" />
                            <span><b>{lastSaved.ean}</b> gespeichert für „{lastSaved.name}" (SKU {lastSaved.sku}) — nächste Artikelnummer eingeben.</span>
                        </div>
                    )}
                </div>
                <p className="text-xs text-gray-500">Noch <strong>{info?.total_without_barcode}</strong> Artikel ohne Barcode. Artikel suchen, dann „Scannen" tippen und den Barcode vor die Kamera halten.</p>
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-emerald-600" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Name, SKU, OEM-Nr." className="pl-8 border-2 border-emerald-500 h-11" data-testid="artscan-search" />
                    </div>
                    <Button onClick={search} className="h-11 bg-[#111827] text-white" data-testid="artscan-search-btn">
                        {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                </div>

                {items.length === 0 ? (
                    <div className="text-center text-sm text-gray-500 py-10" data-testid="artscan-empty">
                        <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        {searching ? "Lädt…" : "Keine offenen Artikel gefunden."}
                    </div>
                ) : (
                    <div className="space-y-2">
                        {items.map((a) => (
                            <div key={a.id} className="bg-white border border-gray-200 rounded-sm p-3" data-testid={`artscan-row-${a.id}`}>
                                <div className="font-bold text-sm">{a.name}</div>
                                <div className="text-[11px] text-gray-500 mb-2">{[a.sku, a.manufacturer, a.location].filter(Boolean).join(" · ")}</div>
                                <Button onClick={() => setScanFor(a.id)} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`artscan-scan-${a.id}`}>
                                    <ScanLine className="w-4 h-4 mr-1" /> Barcode scannen
                                </Button>
                                {scanFor === a.id && (
                                    <EanCameraScanner onDetected={(code) => assign(a, code)} onClose={() => setScanFor(null)} />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
